import { Box, IconButton, Input, SvgIcon, Tooltip } from '@mui/material';
import type React from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { uploadImage as uploadImageAction } from '../../../../actions/aiAssistant.actions';
import {
	borderRadius,
	boxShadows,
	customSizes,
	palette,
	spaces,
} from '../../../../assets/styles/themes/tokens';
import { getCurrentConversationId } from '../../../../reducers/aiAssistant.selectors';
import services from '../../../../services';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import getMuiIcon from '../../../../utils/getMuiIcon';
import useAction from '../../../../utils/useAction';
import { ACCEPTED_IMAGE_TYPES_STRING } from '../constants';
import AIAssistantTextEditorExportButton, {
	type TextEditorDownloadFormat,
} from './AIAssistantTextEditorExportButton';
import { useImageUpload } from './useImageUpload';

const floatingToolbarContainerStyles = {
	position: 'fixed' as const,
	bottom: spaces.md,
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	zIndex: 1301,
	left: 0,
	right: 0,
};

const floatingToolbarStyles = {
	padding: customSizes.floatingToolbar.padding,
	gap: spaces.xs,
	background: palette.background.paperElevation2,
	boxShadow: boxShadows.elevation8,
	borderRadius: borderRadius.sm,
	display: 'flex',
	alignItems: 'center',
	width: 'fit-content',
	justifyContent: 'center',
	flexDirection: 'row' as const,
	height: customSizes.floatingToolbar.height,
};

const AIAssistantTextEditorFloatingToolbar: React.FC = () => {
	const { t } = useTranslation();
	const { textEditorRef } = useContext(aiAssistantEditorContext);
	const uploadImageActionFn = useAction(uploadImageAction);
	const currentConversationId = useSelector(getCurrentConversationId);

	const uploadImage = useCallback(
		async (file: File) => {
			return await uploadImageActionFn({ image: file });
		},
		[uploadImageActionFn]
	);

	const { fileInputRef, handleImageUploadClick, handleFileChange } = useImageUpload({
		editor: textEditorRef,
		uploadImage,
	});

	// The floating toolbar only renders for generated documents (never podcasts),
	// see AIAssistantTextEditorContainer.
	const feature = ['document generation'];

	const onDownload = (format: TextEditorDownloadFormat) => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'download',
			exportFormat: format,
			feature,
		});
	};

	const onPrint = () => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'print',
			exportFormat: 'pdf',
			feature,
		});
	};

	const onShare = () => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'share',
			feature,
		});
	};

	return (
		<>
			<Box component="div" sx={floatingToolbarContainerStyles}>
				<Box role="toolbar" sx={floatingToolbarStyles}>
					<Tooltip title={t('Image')} placement="top">
						<IconButton
							data-testid="floating-toolbar-image-button"
							onClick={handleImageUploadClick}
							sx={{
								padding: spaces.xs,
							}}
						>
							<SvgIcon
								component={getMuiIcon('addIcon')}
								sx={{
									width: 24,
									height: 24,
									fill: palette.text.primary,
								}}
							/>
						</IconButton>
					</Tooltip>
					<AIAssistantTextEditorExportButton
						buttonType="icon"
						showNotification={true}
						menuAnchorOrigin="right"
						onDownload={onDownload}
						onPrint={onPrint}
						onShare={onShare}
					/>
				</Box>
			</Box>
			<Input
				data-testid="floating-toolbar-file-input"
				sx={{ display: 'none' }}
				inputRef={fileInputRef}
				type="file"
				inputProps={{ accept: ACCEPTED_IMAGE_TYPES_STRING }}
				onChange={handleFileChange}
			/>
		</>
	);
};

export default AIAssistantTextEditorFloatingToolbar;
